// src/pages/LinkToolsPage.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './LinkPage.css';
import Seo from '../components/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faChevronLeft,
  faScrewdriverWrench,
  faCalculator,
  faChevronRight,
  faRulerCombined,
  faListCheck,
  faCircleCheck,
  faDoorOpen,
  faTruckRampBox,
  faEnvelopeOpenText,
  faCalendarCheck
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

export default function LinkToolsPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isEmbed = params.has('embed');
  const isFromHome = params.get('from') === 'home';

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 575.98px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  // --- form state ---
  const [m2, setM2] = useState(60);
  const [afwerking, setAfwerking] = useState('standaard');
  const [ondergrond, setOndergrond] = useState('nieuwbouw');
  const [plafonds, setPlafonds] = useState(false);
  const [hoeken, setHoeken] = useState(6);
  const [bereikbaarheid, setBereikbaarheid] = useState('makkelijk');
  const [spoed, setSpoed] = useState(false);

  const [naam, setNaam] = useState('');
  const [bedrijf, setBedrijf] = useState('');
  const [email, setEmail] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [opmerking, setOpmerking] = useState('');

  const [showPostSubmit, setShowPostSubmit] = useState(false);

  const indicatie = useMemo(() => {
    const base = afwerking === 'sausklaar' ? 24 : 18;
    const m2Val = Math.max(1, Number(m2) || 1);
    const ondergrondFactor = ondergrond === 'bestaand' ? 1.10 : 1.0;
    const plafondFactor = plafonds ? 1.10 : 1.0;
    const bereikFactor = bereikbaarheid === 'lastig' ? 1.15 : 1.0;
    const spoedFactor = spoed ? 1.12 : 1.0;
    const hoekenToeslag = Math.max(0, Number(hoeken) || 0) * 12;
    const materiaalArbeid = m2Val * base * ondergrondFactor * plafondFactor * bereikFactor * spoedFactor;
    const subtotaal = materiaalArbeid + hoekenToeslag;
    const minimum = 250;
    return Math.round(Math.max(subtotaal, minimum));
  }, [m2, afwerking, ondergrond, plafonds, hoeken, bereikbaarheid, spoed]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const onderwerp = encodeURIComponent(`Aanvraag stuc indicatie – ± €${indicatie} excl. btw`);
    const body = encodeURIComponent(
`Beste Online Growth Club,

Ik wil graag sparren over een rekentool/offerte voor stucwerk.

Gegevens:
- Oppervlakte: ${m2} m²
- Afwerking: ${afwerking}
- Ondergrond: ${ondergrond}
- Plafonds: ${plafonds ? 'ja' : 'nee'}
- Hoeken/kozijnen: ${hoeken}
- Bereikbaarheid: ${bereikbaarheid}
- Spoed: ${spoed ? 'ja' : 'nee'}

Indicatieve prijs (excl. btw): ~ €${indicatie}

Contact:
- Naam: ${naam}
- Bedrijf: ${bedrijf}
- E-mail: ${email}
- Telefoon: ${telefoon}

Extra info:
${opmerking || '(geen)'}
`
    );
    setShowPostSubmit(true);
    setTimeout(() => {
      window.location.href = `mailto:info@onlinegrowthclub.nl?subject=${onderwerp}&body=${body}`;
    }, 100);
  };

  // Verberg het eerste pitch-blok ALLEEN op mobiel + embed + from=home
  const hideIntroPitch = isEmbed && isFromHome && isMobile;

  return (
    <>
      <Seo
        title="Rekentools | Online Growth Club"
        description="Slimme rekentools voor bouw en offerte-diensten. Voorbeeld: stucadoor m²-tool met indicatieve prijs en contact."
        path="/links/tools"
      />

      <main className={`linkpage-hero ${isEmbed ? 'is-embed' : ''}`}>
        <div className="lp-container">
          {/* Top bar */}
          <div className="lp-tools-top">
            {!isEmbed && (
              <Link to="/links" className="lp-back" aria-label="Terug naar links">
                <FontAwesomeIcon icon={faChevronLeft} />
                <span>Terug naar links</span>
              </Link>
            )}
            <Link to="/" className="navbar-brand d-flex align-items-center gap-2 lp-logo">
              <FontAwesomeIcon icon={faRocket} className="logo-icon text-orange" />
              <span className="logo-title">
                Online<span className="text-orange">Growth</span>Club
              </span>
            </Link>
          </div>

          {/* Intro/pitch — conditioneel tonen */}
          {!hideIntroPitch && (
            <section className="lp-card">
              <div className="lp-card-head">
                <div className="lp-card-icon">
                  <FontAwesomeIcon icon={faCalculator} />
                </div>
                <h1 className="lp-card-title">Rekentools die tijd besparen</h1>
              </div>
              <p className="lp-card-lead">
                Voor bouwbedrijven én alle services waarbij vooraf een prijsindicatie nodig is
                (bijv. stukadoren, schilderen, vloeren, schoonmaak, dakkapellen, zonnepanelen).
                Klanten krijgen direct een bandbreedte, jij ontvangt gerichte leads. Minder telefoontjes,
                snellere offertes, meer rust in je planning.
              </p>
            </section>
          )}

          {/* Tool */}
          <section className="lp-card">
            <div className="lp-card-head">
              <div className="lp-card-icon">
                <FontAwesomeIcon icon={faScrewdriverWrench} />
              </div>
              <h2 className="lp-card-title">Stucadoor m²-tool (voorbeeld)</h2>
            </div>

            <div className="lp-grid2">
              <div className="lp-field">
                <span>Oppervlakte</span>
                <div className="lp-input">
                  <FontAwesomeIcon icon={faRulerCombined} className="lp-input-icon" />
                  <input
                    type="number"
                    min="1"
                    value={m2}
                    onChange={(e) => setM2(e.target.value)}
                    placeholder="Bijv. 60"
                  />
                  <span className="unit-badge">m²</span>
                </div>
              </div>

              <div className="lp-field">
                <span>Afwerking</span>
                <div className="lp-input">
                  <FontAwesomeIcon icon={faListCheck} className="lp-input-icon" />
                  <select value={afwerking} onChange={(e) => setAfwerking(e.target.value)}>
                    <option value="standaard">Standaard (v.a. €18/m²)</option>
                    <option value="sausklaar">Sausklaar (v.a. €24/m²)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="lp-grid2">
              <div className="lp-field">
                <span>Ondergrond</span>
                <div className="lp-input">
                  <FontAwesomeIcon icon={faCircleCheck} className="lp-input-icon" />
                  <select value={ondergrond} onChange={(e) => setOndergrond(e.target.value)}>
                    <option value="nieuwbouw">Nieuwbouw (vlak)</option>
                    <option value="bestaand">Bestaand (oneffen)</option>
                  </select>
                </div>
              </div>

              <div className="lp-field">
                <span>Bereikbaarheid</span>
                <div className="lp-input">
                  <FontAwesomeIcon icon={faTruckRampBox} className="lp-input-icon" />
                  <select value={bereikbaarheid} onChange={(e) => setBereikbaarheid(e.target.value)}>
                    <option value="makkelijk">Makkelijk (normale toegang)</option>
                    <option value="lastig">Lastig (steiger/smal)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="lp-grid3">
              <div className="lp-field">
                <span>Hoeken / kozijnen</span>
                <div className="lp-input">
                  <FontAwesomeIcon icon={faDoorOpen} className="lp-input-icon" />
                  <input
                    type="number"
                    min="0"
                    value={hoeken}
                    onChange={(e) => setHoeken(e.target.value)}
                    placeholder="Bijv. 6"
                  />
                  <span className="unit-badge">aantal</span>
                </div>
              </div>

              <div className="lp-field">
                <span>Plafonds meenemen</span>
                <label className="lp-toggle">
                  <input
                    type="checkbox"
                    checked={plafonds}
                    onChange={(e) => setPlafonds(e.target.checked)}
                  />
                  <span className="lp-toggle-ui"></span>
                  <span className="lp-toggle-label">+10%</span>
                </label>
              </div>

              <div className="lp-field">
                <span>Spoed</span>
                <label className="lp-toggle">
                  <input
                    type="checkbox"
                    checked={spoed}
                    onChange={(e) => setSpoed(e.target.checked)}
                  />
                  <span className="lp-toggle-ui"></span>
                  <span className="lp-toggle-label">+12%</span>
                </label>
              </div>
            </div>

            <p className="lp-result">
              Richtprijs (indicatie, excl. btw): <strong>€{indicatie}</strong>
            </p>

            <div className="lp-pitch-inline">
              <p>
                Wil jij ook minder beldruk en sneller offertes? Wij bouwen deze tool op maat in je huisstijl.
                Bezoekers krijgen direct een indicatie; jij ontvangt gerichte leads met alle context.
              </p>
            </div>
          </section>

          {/* Contactformulier */}
          <section className="lp-card">
            <div className="lp-card-head">
              <div className="lp-card-icon">
                <FontAwesomeIcon icon={faEnvelopeOpenText} />
              </div>
              <h2 className="lp-card-title">Vrijblijvend contact met indicatie</h2>
            </div>

            <form onSubmit={handleSubmit} className="lp-form">
              <div className="lp-grid2">
                <div className="lp-field">
                  <span>Naam</span>
                  <div className="lp-input">
                    <input value={naam} onChange={(e) => setNaam(e.target.value)} required placeholder="Voor- en achternaam" />
                  </div>
                </div>
                <div className="lp-field">
                  <span>Bedrijf</span>
                  <div className="lp-input">
                    <input value={bedrijf} onChange={(e) => setBedrijf(e.target.value)} placeholder="Optioneel" />
                  </div>
                </div>
              </div>

              <div className="lp-grid2">
                <div className="lp-field">
                  <span>E-mail</span>
                  <div className="lp-input">
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="jij@bedrijf.nl" />
                  </div>
                </div>
                <div className="lp-field">
                  <span>Telefoon</span>
                  <div className="lp-input">
                    <input value={telefoon} onChange={(e) => setTelefoon(e.target.value)} placeholder="06-…" />
                  </div>
                </div>
              </div>

              <div className="lp-field">
                <span>Korte omschrijving</span>
                <div className="lp-input">
                  <textarea rows="4" value={opmerking} onChange={(e) => setOpmerking(e.target.value)} placeholder="Vertel kort wat je wilt laten doen" />
                </div>
              </div>

              <div className="lp-pitch-inline">
                <p>Huidige indicatie op basis van je input: <strong>± €{indicatie} excl. btw</strong>.</p>
              </div>

              <button type="submit" className="lp-cta-big" style={{ display: 'inline-block' }}>
                Verstuur aanvraag met indicatie
              </button>
            </form>

            {!isEmbed && (
              <div className="lp-tools-bottom">
                <Link to="/links" className="lp-back mt-3" aria-label="Terug naar links">
                  <FontAwesomeIcon icon={faChevronLeft} />
                  <span>Terug naar links</span>
                </Link>
              </div>
            )}
          </section>
        </div>
      </main>
      </>
  );
}
